<script setup>
import { ref, computed } from "vue";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { Empty } from "@/shared/ui/empty";
import { Skeleton } from "@/shared/ui/skeleton";
import Button from "@/shared/ui/button/Button.vue";
import { Separator } from "@/shared/ui/separator";
import { Spinner } from "@/shared/ui/spinner";

import UserProfileSidebar from "./UserProfileSidebar.vue";
import PersonalTab from "./tabs/PersonalTab.vue";

import { useUserProfileDrawerData } from "../composables/useUserProfileDrawerData";
import { cn } from "@/shared/lib/utils";

const isOpen = ref(false);
const tab = ref("personal");

const { state, dirty, canEdit, load, update, cancel, save } =
  useUserProfileDrawerData();

const showEmpty = computed(
  () => !state.loading && (state.error || !Object.keys(state.data || {}).length)
);

async function open() {
  isOpen.value = true;
  tab.value = "personal";
  await load();
}

function requestClose() {
  if (state.editMode && dirty.value) {
    if (
      !window.confirm(
        "Несохранённые изменения будут потеряны. Закрыть профиль?"
      )
    ) {
      return;
    }
  }
  doClose();
}

function doClose() {
  isOpen.value = false;
  state.editMode = false;
}

function handleOpenChange(val) {
  if (!val) requestClose();
}

function onEditClick() {
  if (state.loading || showEmpty.value) return;
  if (!canEdit.value) return;
  state.editMode = true;
}

async function onSave() {
  try {
    await save();
  } catch (e) {
    console.error(e);
  }
}

function onCancel() {
  cancel();
}

function onPointerDownOutside(event) {
  if (state.editMode && dirty.value) {
    event.preventDefault();
    if (
      window.confirm("Несохранённые изменения будут потеряны. Закрыть профиль?")
    ) {
      doClose();
    }
  }
}

defineExpose({ open, close: doClose });
</script>

<template>
  <Sheet :open="isOpen" @update:open="handleOpenChange">
    <SheetContent
      side="right"
      :class="
        cn(
          'p-0 flex flex-col gap-0 sm:max-w-none [&>button]:hidden border border-solid border-border shadow-sm bg-card'
        )
      "
      :style="{ width: 'min(1080px, 96vw)', maxWidth: 'min(1080px, 96vw)' }"
      @pointer-down-outside="onPointerDownOutside"
    >
      <SheetHeader class="sr-only">
        <SheetTitle>Профиль</SheetTitle>
      </SheetHeader>

      <div class="flex-1 flex min-h-0 overflow-hidden">
        <template v-if="state.loading">
          <aside
            class="w-[290px] shrink-0 bg-card border-r border-border pb-5 flex flex-col gap-2 overflow-hidden"
          >
            <div class="overflow-hidden bg-card">
              <div class="h-24 bg-muted border-0 border-b border-border" />
              <div class="px-4 pt-0 pb-2 -mt-10">
                <Skeleton
                  class="w-16 h-16 rounded-full mb-3 ring-4 ring-card"
                />
                <Skeleton class="h-4 w-40 mb-2" />
                <Skeleton class="h-3 w-24" />
              </div>
            </div>
            <Separator />
          </aside>
          <main class="flex-1 bg-[hsl(0_0%_98%)] overflow-y-auto">
            <div class="max-w-[800px] mx-auto p-6 flex flex-col gap-4">
              <Skeleton
                class="h-40 w-full"
                style="border-radius: var(--radius)"
              />
              <Skeleton
                class="h-40 w-full"
                style="border-radius: var(--radius)"
              />
            </div>
          </main>
        </template>

        <template v-else-if="showEmpty">
          <main
            class="flex-1 bg-[hsl(0_0%_98%)] overflow-y-auto flex items-center justify-center"
          >
            <Empty
              title="Профиль не найден"
              description="Не удалось загрузить данные профиля."
            >
              <template #footer>
                <Button @click="load()">Повторить</Button>
              </template>
            </Empty>
          </main>
        </template>

        <template v-else>
          <UserProfileSidebar
            :data="state.data"
            :tab="tab"
            :edit-mode="state.editMode"
            :saving="state.saving"
            :can-edit="canEdit"
            @set-tab="tab = $event"
            @start-edit="onEditClick"
            @cancel="onCancel"
          />

          <main class="flex-1 bg-[hsl(0_0%_98%)] overflow-y-auto">
            <div class="max-w-[800px] mx-auto p-6">
              <PersonalTab
                :data="state.data"
                :edit-mode="state.editMode"
                :can-edit="canEdit"
                @update="update"
              />

              <div
                v-if="state.editMode && canEdit"
                class="flex justify-end mt-6"
              >
                <Button
                  size="sm"
                  :disabled="state.saving || !dirty"
                  @click="onSave"
                >
                  <Spinner
                    v-if="state.saving"
                    size="sm"
                    class="mr-1.5 text-current"
                  />
                  {{ state.saving ? "Сохраняем…" : "Сохранить" }}
                </Button>
              </div>
            </div>
          </main>
        </template>
      </div>
    </SheetContent>
  </Sheet>
</template>
